import { assertUnreachable } from '@disy/lib/custom-error';

import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';

/// @see java/net/disy/cadenza/repository/api/qlexpression/IQlExpression.java et al.
export type PredicateQlExpression =
  | ComparisonQlExpression
  | NotQlExpression
  | InQlExpression
  | MultiArityQlExpression
  | LikeQlExpression;
export type LiteralQlExpression =
  | NullLiteralQlExpression
  | NumberLiteralQlExpression
  | DateTimeLiteralQlExpression
  | StringLiteralQlExpression
  | VariableLiteralQlExpression;
export type ValueQlExpression = ArithmeticQlExpression | LiteralQlExpression | QueryElementQlExpression;
export type QlExpression = PredicateQlExpression | ValueQlExpression;
export type QlMode = 'Condition' | 'Attribute';

interface BaseExpressionDto<T extends ExpressionType> {
  expressionType: T;
  /** holds trivia preceding the expression node */
  triviaBefore?: string;
  /** holds trivia succeeding the expression node */
  triviaAfter?: string;
}

type ExpressionType =
  | 'binaryExpression'
  | 'arithmeticExpression'
  | 'notExpression'
  | 'inExpression'
  | 'nullLiteralExpression'
  | 'numberLiteralExpression'
  | 'dateTimeLiteralExpression'
  | 'stringLiteralExpression'
  | 'variableLiteralExpression'
  | 'multiArityExpression'
  | 'likeExpression'
  | 'queryElementExpression';

export interface ComparisonQlExpression extends BaseExpressionDto<'binaryExpression'> {
  operator: ComparisonQlExpressionOperator;
  lhs: ValueQlExpression;
  /** `string` holds arbitrary casing of `IS` */
  triviaOperator?: string;
  rhs: ValueQlExpression;
}

const COMPARISON_OPS_TO_SYMBOLS = {
  EQUALS: '=',
  NOT_EQUALS: '!=',
  LESS_THAN: '<',
  LESS_THAN_EQUALS: '<=',
  GREATER_THAN: '>',
  GREATER_THAN_EQUALS: '>=',
} as const;
export type ComparisonQlExpressionOperator = keyof typeof COMPARISON_OPS_TO_SYMBOLS;
export const ComparisonQlExpressionOperator = {
  toSymbol: (exprOp: ComparisonQlExpressionOperator) => COMPARISON_OPS_TO_SYMBOLS[exprOp],
};

export interface ArithmeticQlExpression extends BaseExpressionDto<'arithmeticExpression'> {
  operator: ArithmeticQlExpressionOperator;
  lhs: ValueQlExpression;
  rhs: ValueQlExpression;
}

const ARITHMETIC_OPS_TO_SYMBOLS = {
  ADD: '+',
  SUB: '-',
  MUL: '*',
  DIV: '/',
} as const;
export type ArithmeticQlExpressionOperator = keyof typeof ARITHMETIC_OPS_TO_SYMBOLS;
export const ArithmeticQlExpressionOperator = {
  toSymbol: (exprOp: ArithmeticQlExpressionOperator) => ARITHMETIC_OPS_TO_SYMBOLS[exprOp],
};

export interface NotQlExpression extends BaseExpressionDto<'notExpression'> {
  operand: PredicateQlExpression;
  /** holds arbitrary casing of `NOT` */
  triviaOperator?: string;
}

export interface InQlExpression extends BaseExpressionDto<'inExpression'> {
  lhs: QueryElementQlExpression;
  /** holds arbitrary casing of `IN` */
  triviaOperator?: string;
  literals: LiteralQlExpression[];
}

interface NullLiteralQlExpression extends BaseExpressionDto<'nullLiteralExpression'> {
  triviaLiteral?: string;
}

type BaseLiteralExpressionDto<T extends ExpressionType, V> = BaseExpressionDto<T> & { value: V };
type NumberLiteralQlExpression = BaseLiteralExpressionDto<'numberLiteralExpression', string>;
type DateTimeLiteralQlExpression = BaseLiteralExpressionDto<'dateTimeLiteralExpression', Date>;
type StringLiteralQlExpression = BaseLiteralExpressionDto<'stringLiteralExpression', string>;
type VariableLiteralQlExpression = BaseLiteralExpressionDto<'variableLiteralExpression', string>;

interface MultiArityQlExpression extends BaseExpressionDto<'multiArityExpression'> {
  type: MultiArityExpressionOperator;
  operands: PredicateQlExpression[];
  /** holds arbitrary casing of `AND`s and `OR`s */
  triviaOperators?: string[];
}

export type MultiArityExpressionOperator = 'AND' | 'OR';

export interface LikeQlExpression extends BaseExpressionDto<'likeExpression'> {
  queryElement: QueryElementQlExpression;
  /** holds arbitrary casing of `LIKE` */
  triviaOperator?: string;
  searchTerm: StringLiteralQlExpression | VariableLiteralQlExpression;
}

export interface QueryElementQlExpression extends BaseExpressionDto<'queryElementExpression'> {
  attributeId: AttributeId;
}

export function isPredicateQlExpression(expression: QlExpression): expression is PredicateQlExpression {
  switch (expression.expressionType) {
    case 'binaryExpression':
    case 'notExpression':
    case 'inExpression':
    case 'multiArityExpression':
    case 'likeExpression':
      return true;
    case 'arithmeticExpression':
    case 'nullLiteralExpression':
    case 'numberLiteralExpression':
    case 'dateTimeLiteralExpression':
    case 'stringLiteralExpression':
    case 'variableLiteralExpression':
    case 'queryElementExpression':
      return false;
    default:
      assertUnreachable(expression);
  }
}

export function isValueQlExpression(expression: QlExpression): expression is ValueQlExpression {
  return !isPredicateQlExpression(expression);
}
